import { useEffect, useRef } from 'react';
import liveChatApi from '../services/liveChatApi';
import { playerReadAcknowledgement } from '../services/playerChatRead';

export default function usePlayerChatRead({ roomId, messages, agent, enabled, onRead, panelRef }) {
  const payload = playerReadAcknowledgement(messages, roomId, agent);
  const signature = payload ? `${roomId}|${JSON.stringify(payload)}` : '';
  const current = useRef(null);
  current.current = { signature, enabled, onRead };
  const completed = useRef(new Set());

  useEffect(() => {
    if (!enabled || !signature || completed.current.has(signature)) return undefined;
    let disposed = false;
    let pending = false;
    let timer;
    const isVisible = () => !disposed && current.current.enabled
      && current.current.signature === signature && document.visibilityState === 'visible'
      && Boolean(panelRef.current?.getClientRects().length);
    const acknowledge = async () => {
      if (pending || !isVisible() || completed.current.has(signature)) return;
      pending = true;
      try {
        await liveChatApi.post(`/api/chat/conversations/${encodeURIComponent(roomId)}/read`, payload);
        completed.current.add(signature);
        if (completed.current.size > 100) completed.current.delete(completed.current.values().next().value);
        if (!disposed) current.current.onRead?.();
      } catch {
        // Do not clear badges on failure. Retry only while this stream is visible.
        if (!disposed) timer = setTimeout(acknowledge, 5000);
      } finally {
        pending = false;
      }
    };
    const resume = () => {
      clearTimeout(timer);
      if (isVisible()) timer = setTimeout(acknowledge, 140);
    };
    resume();
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    return () => {
      disposed = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
    };
    // Payload is represented completely by signature; callbacks are kept current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, signature, roomId, panelRef]);
}
